export class ReleaseScheduleError extends Error {}

export const MAX_RELEASE_SELECTION = 1000;
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export type ReleasePlan = {
    date: string;
    slot: string;
    timeZone: string;
};

function dateFormatter(timeZone: string) {
    if (typeof timeZone !== "string" || timeZone.length > 100) throw new ReleaseScheduleError("Choose a valid time zone");
    try {
        return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
    } catch { throw new ReleaseScheduleError("Choose a valid time zone"); }
}

export function localDate(date: Date, timeZone: string): string {
    const parts = dateFormatter(timeZone).formatToParts(date);
    return ["year", "month", "day"].map(type => parts.find(part => part.type === type)!.value).join("-");
}

export function addCalendarDays(date: string, days: number): string {
    const value = new Date(`${date}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(value.getTime()) || value.toISOString().slice(0, 10) !== date) {
        throw new ReleaseScheduleError("Choose a valid release date");
    }
    return new Date(value.getTime() + days * DAY).toISOString().slice(0, 10);
}

/** A single global hourly scheduler; labels always show the actual local slot. */
export function hourlySlots(date: string, timeZone: string) {
    const validDate = addCalendarDays(date, 0);
    const formatter = dateFormatter(timeZone);
    const time = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    const offset = new Intl.DateTimeFormat("en-GB", { timeZone, timeZoneName: "shortOffset" });
    const middle = new Date(`${validDate}T00:00:00Z`).getTime();
    const slots: { value: string; label: string; offset: string }[] = [];
    for (let instant = middle - DAY; instant < middle + 2 * DAY; instant += HOUR) {
        const value = new Date(instant);
        const parts = formatter.formatToParts(value);
        if (["year", "month", "day"].map(type => parts.find(part => part.type === type)!.value).join("-") === date) {
            slots.push({ value: value.toISOString(), label: time.format(value), offset: offset.formatToParts(value).find(part => part.type === "timeZoneName")!.value });
        }
    }
    return slots;
}

export function validateReleaseDates(values: string[], timeZone: string, now = new Date()): Date[] {
    const today = localDate(now, timeZone);
    if (!Array.isArray(values) || !values.length || values.length > MAX_RELEASE_SELECTION) throw new ReleaseScheduleError(`Select 1–${MAX_RELEASE_SELECTION} chapters`);
    const dates = values.map(value => {
        if (typeof value !== "string") throw new ReleaseScheduleError("Invalid release time");
        const date = new Date(value);
        if (!Number.isFinite(date.getTime()) || date.getTime() % HOUR !== 0) throw new ReleaseScheduleError("Choose an available hourly release slot");
        if (localDate(date, timeZone) <= today || date <= now) throw new ReleaseScheduleError("Choose tomorrow or a later date");
        return date;
    });
    if (dates.some(date => date.getTime() !== dates[0].getTime())) throw new ReleaseScheduleError("Selected chapters must release together");
    return dates;
}

export function buildReleaseDates(plan: ReleasePlan, count: number): string[] {
    if (!Number.isInteger(count) || count < 1 || count > MAX_RELEASE_SELECTION) throw new ReleaseScheduleError(`Select 1–${MAX_RELEASE_SELECTION} chapters`);
    if (!plan) throw new ReleaseScheduleError("Choose a release date and hour");
    // Keep the exact selected instant, including a repeated DST hour.
    const chosen = hourlySlots(plan.date, plan.timeZone).find(slot => slot.value === plan.slot);
    if (!chosen) throw new ReleaseScheduleError(`That hour is not available on ${plan.date}; choose another hour`);
    return Array.from({ length: count }, () => chosen.value);
}

export function formatRelease(value: string | Date, timeZone: string): string {
    return new Intl.DateTimeFormat("en", { timeZone, dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}
